/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import ConnectionError from '../../../../shared/ErrorComponents/ConnectionError';
import withErrorStatus from '../../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../../shared/StatusWrappers/LoadingStatus';
import useConnectorAndDynamicProps from '../../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import { useInterval } from '../../../../shared/useInterval/useInterval';
import useMounted from '../../../../shared/useMount/useMount';
import {
  POLLING_SECONDS,
  POLLING_MILLI_SECONDS,
  UtilityType,
  IntervalType
} from '../../../../shared/Utils/Constants';
import { isObjectOrArrayAndEmpty } from '../../../../shared/Utils/Utils';
import {
  ISiteCard,
  IEmaasWaterUsageHOCProps,
  IEuiSummary,
  IEmaasWaterUsageComponentData
} from '../../../IEnergyManagementProps';
import EmaasBasicSubscriptionEnergyUsageComponent from '../../../EmaasEnergyUsage/EmaasBasicSubscriptionEnergyUsageComponent/EmaasBasicSubscriptionEnergyUsageComponent';
import {
  EmaasSubscriptionType,
  EnergyUsageField
} from '../../../../shared/Enums';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import {
  IPollingContext,
  PollingContext
} from '../../../../../context/PollingContext/PollingContext';
import { getIntervalType } from '../../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import moment from 'moment';
import NoData from '../../../../shared/ErrorComponents/NoData';

interface IPortfolioWaterHOCContent extends ISiteCard {
  data: IEmaasWaterUsageComponentData;
  dataSourceType?: string;
}

const PortfolioWaterHOCContent = (props: IPortfolioWaterHOCContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div>
      <EmaasBasicSubscriptionEnergyUsageComponent
        data={data}
        viewType={EnergyUsageField.PORTFOLIOWATER}
        dataSourceType={props.dataSourceType}
      />
    </div>
  );
};

const PortfolioWaterHOCWithLoader = withLoadingStatus(
  withErrorStatus(PortfolioWaterHOCContent)
);

// Refresh every hour
const ENERGYUSAGE_REFRESH_INTERVAL = POLLING_SECONDS * POLLING_MILLI_SECONDS;

const PortfolioWaterHOC = (props: IEmaasWaterUsageHOCProps) => {
  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const [pollingCount, setPollingCount] = useState(0);
  const { bearerToken, requestContext } = props;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );

  const isDateRangeValid = () => {
    const sDate = moment(
      getEnergyStartDate(
        requestContext.context.startTime!,
        requestContext.context.subscriptionType!
      ),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      getEnergyEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const connectorConfig = {
    name: 'portfolio-energy-usage',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: 'Water',
      intervalType: IntervalType.Monthly //getIntervalType(startDate, endDate) // Portfolio water usage is only available monthly
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const onRefresh = () => dataConnector?.actions?.getAll();

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [pollingCount]);

  useEffect(() => {
    setIsSilentLoading(false);
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        onRefresh();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  const { all, loading, error, status } = dataConnector.data;

  const removeSilentLoadingonRefresh = () => {
    setIsSilentLoading(false);
    return onRefresh();
  };

  const EmaasEnergyUsage = (all as unknown) as IEmaasWaterUsageComponentData;

  // Polling mechanism
  useInterval(() => {
    if (
      EmaasEnergyUsage &&
      EmaasEnergyUsage.subscriptionType === EnergyUsageField.PREMIUMSUBS
    ) {
      setIsSilentLoading(true);
      setPollingCount(pollingCount + 1);
    }
  }, ENERGYUSAGE_REFRESH_INTERVAL);

  const isDataAvailable = EmaasEnergyUsage ? true : false;
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(status);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IEmaasWaterUsageComponentData);
  // const pollingContext = useContext(PollingContext);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(EmaasEnergyUsage);
        // Removing polling context for water as EUI affected
        // setContextData(pollingContext, EmaasEnergyUsage);
      }
      setErrorInfo(error);
    }
    setStatusCode(status);
  }, [isDataAvailable, loading, status]);

  return isOutOfRange ? (
    <div className="noDataDisplay common-error-container">
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <PortfolioWaterHOCWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={statusCode}
      onRefresh={removeSilentLoadingonRefresh}
      data={data}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
      silentLoading={silentLoading}
      dataSourceType={props.dataSourceType}
    />
  );
};

const setContextData = (
  pollingContext: IPollingContext,
  EmaasEnergyUsage: IEmaasWaterUsageComponentData
) => {
  const { setPollingTime, setEuiSummary } = pollingContext;

  setPollingTime && setPollingTime();
  setEuiSummary &&
    setEuiSummary(EmaasEnergyUsage?.euiSummary || ({} as IEuiSummary));
};

export default PortfolioWaterHOC;
